import React, { useMemo, useEffect, useRef } from 'react';
import { SignUp, useAuth } from '@clerk/clerk-react';
import { PageShell } from './layout';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from '../styles/AuthPage.module.css';
import { maybeTrackAuthCtaFromAuthPage, trackAuthPageViewed } from '../analytics';
import { isGameplayAccessReason } from '../utils/gameplayAccess';
import useAuthReturnPath from '../hooks/useAuthReturnPath';
const MONDIALI_STADIUM_IMAGE_URL = `${process.env.PUBLIC_URL || ''}/images/mondiali_bg.jpeg`;
const isMondialiPath = (path = '') => path === '/mondiali' || path.startsWith('/mondiali/');

/**
 * Simple skeleton placeholder that matches the form size
 */
const AuthFormSkeleton = () => (
  <div className={styles.skeleton} />
);

const AuthContentLayout = ({ hideNav, onBack, children }) => {
  if (hideNav) {
    return children;
  }
  return <PageShell onBack={onBack} showStickyAdRail={false}>{children}</PageShell>;
};

/**
 * Custom Sign Up Page
 * 
 * Uses Clerk's SignUp component embedded in our own page layout.
 * Supports a `next` query parameter for post-authentication redirection.
 * Localization is handled automatically via ClerkProvider in index.js.
 */
const SignUpPage = () => {
  const authStartedAtRef = useRef(Date.now());
  const hasRedirectedRef = useRef(false);
  const POST_AUTH_RESOURCE_ALERTS_HINT_KEY = 'post-auth-resource-alerts-hint';
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Extract and validate query parameters
  const { nextParam, clerkRedirectParam, message, sourceParam, gateReasonParam } = useMemo(() => {
    let next = null;
    let msg = null;
    let source = null;
    let gateReason = null;
    let clerkRedirect = null;
    try {
      const params = new URLSearchParams(location.search);
      const nextVal = params.get('next');
      clerkRedirect = params.get('redirect_url');
      // Only allow internal paths (starting with / but not //)
      if (nextVal && nextVal.startsWith('/') && !nextVal.startsWith('//')) {
        next = nextVal;
      }
      // Get optional message to display
      msg = params.get('message') || null;
      source = params.get('source') || null;
      gateReason = params.get('gate_reason') || null;
    } catch (_) {}
    return { nextParam: next, clerkRedirectParam: clerkRedirect, message: msg, sourceParam: source, gateReasonParam: gateReason };
  }, [location.search]);
  const { afterAuthUrl, forceRedirectUrl } = useAuthReturnPath(nextParam, isSignedIn, clerkRedirectParam);

  const resolvedSourceParam = useMemo(() => {
    if (sourceParam) return sourceParam;
    return isMondialiPath(afterAuthUrl) ? 'mondiali' : null;
  }, [afterAuthUrl, sourceParam]);
  const isMondialiFlow = resolvedSourceParam === 'mondiali';
  const containerClassName = `${styles.container} ${isMondialiFlow ? styles.mondialiFlowBackground : ''}`.trim();
  const mondialiBackgroundStyle = isMondialiFlow
    ? { '--mondiali-stadium-image': `url("${MONDIALI_STADIUM_IMAGE_URL}")` }
    : undefined;

  // Build signInUrl preserving redirect and tracking params.
  // Activity reward messages apply to both auth flows. Other signup copy stays here.
  // Fall back to afterAuthUrl so cross-auth links keep redirect intent
  // even when current URL params were stripped.
  const signInUrl = useMemo(() => {
    const params = new URLSearchParams();
    const redirectNext = nextParam || (afterAuthUrl !== '/' ? afterAuthUrl : null);
    if (redirectNext) {
      params.set('next', redirectNext);
      params.set('redirect_url', redirectNext);
    }
    if ((gateReasonParam === 'claim_guest_rewards' || isGameplayAccessReason(gateReasonParam)) && message) params.set('message', message);
    else params.set('clear_message', '1');
    if (resolvedSourceParam) params.set('source', resolvedSourceParam);
    if (gateReasonParam) params.set('gate_reason', gateReasonParam);
    const qs = params.toString();
    return qs ? `/signin?${qs}` : '/signin';
  }, [nextParam, resolvedSourceParam, gateReasonParam, afterAuthUrl, message]);

  useEffect(() => {
    if (!isLoaded || isSignedIn) return;
    maybeTrackAuthCtaFromAuthPage({
      source_surface: resolvedSourceParam || null,
      gate_reason: gateReasonParam || null,
      cta_kind: 'sign_up',
      next_path: afterAuthUrl,
    });
  }, [afterAuthUrl, gateReasonParam, isLoaded, isSignedIn, resolvedSourceParam]);

  useEffect(() => {
    trackAuthPageViewed({
      auth_mode: 'sign_up',
      source_surface: resolvedSourceParam || 'auth_page_direct',
      gate_reason: gateReasonParam || null,
      next_path: afterAuthUrl,
    });
  }, [afterAuthUrl, resolvedSourceParam, gateReasonParam]);

  // Handle redirect after successful sign-up
  useEffect(() => {
    if (isLoaded && isSignedIn && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      try {
        sessionStorage.setItem(
          POST_AUTH_RESOURCE_ALERTS_HINT_KEY,
          JSON.stringify({
            reason: 'account_signup',
            started_at_ms: authStartedAtRef.current,
            source_surface: resolvedSourceParam || null,
            created_at_ms: Date.now(),
          }),
        );
      } catch (_) {}
      // Clear the stored redirect
      try { sessionStorage.removeItem('auth-redirect-next'); } catch (_) {}
      navigate(afterAuthUrl, { replace: true });
    }
  }, [isLoaded, isSignedIn, afterAuthUrl, navigate, resolvedSourceParam]);

  // Show skeleton while Clerk is loading
  if (!isLoaded) {
    return (
      <AuthContentLayout hideNav={isMondialiFlow} onBack={() => navigate(-1)}>
        <div className={containerClassName} style={mondialiBackgroundStyle}>
          <div className={styles.clerkWrapper}>
            <AuthFormSkeleton />
          </div>
        </div>
      </AuthContentLayout>
    );
  }

  // If already signed in, the useEffect above will handle the redirect
  if (isSignedIn) {
    return (
      <AuthContentLayout hideNav={isMondialiFlow}>
        <div className={containerClassName} style={mondialiBackgroundStyle}>
          <div className={styles.clerkWrapper}>
            <AuthFormSkeleton />
          </div>
        </div>
      </AuthContentLayout>
    );
  }

  return (
    <AuthContentLayout hideNav={isMondialiFlow} onBack={() => navigate(-1)}>
      <div className={containerClassName} style={mondialiBackgroundStyle}>
        <div className={styles.clerkWrapper}>
          {message && (
            <div className={styles.messageBox}>
              <p className={styles.messageText}>{message}</p>
            </div>
          )}
          <SignUp 
            routing="path"
            path="/signup"
            signInUrl={signInUrl}
            fallbackRedirectUrl={afterAuthUrl}
            forceRedirectUrl={forceRedirectUrl}
            signInFallbackRedirectUrl={afterAuthUrl}
            signInForceRedirectUrl={forceRedirectUrl}
            appearance={{
              elements: {
                rootBox: styles.clerkRoot,
                card: styles.clerkCard,
                headerTitle: styles.clerkCardHeader,
                headerSubtitle: styles.clerkCardHeader,
                socialButtonsBlockButton: styles.clerkSocialButton,
                formFieldLabel: styles.clerkFormFieldLabel,
                formFieldLabelRow: styles.clerkFormFieldLabelRow,
                formFieldInput: styles.clerkFormFieldInput,
                formFieldHintText: styles.clerkFormFieldHintText,
                formFieldErrorText: styles.clerkFormFieldErrorText,
                formFieldSuccessText: styles.clerkFormFieldSuccessText,
                identityPreview: styles.clerkIdentityPreview,
                identityPreviewText: styles.clerkIdentityPreviewText,
                identityPreviewEditButton: styles.clerkIdentityPreviewEditButton,
                formButtonPrimary: styles.clerkFormButtonPrimary,
                footer: styles.clerkFooter,
                footerActionLink: styles.clerkFooterActionLink,
                dividerLine: styles.clerkDividerLine,
                dividerText: styles.clerkDividerText,
                logoBox: styles.clerkLogoBox,
                main: styles.clerkMain,
              }
            }}
          />
        </div>
      </div>
    </AuthContentLayout>
  );
};

export default SignUpPage;
